import { useEffect, useState } from 'react';

/**
 * A computer, as opposed to something carried to the bench.
 *
 * The distinction is not screen size on its own — a tablet propped next to an
 * instrument is wide and still a capture device, and a laptop in tablet mode is
 * not. What separates them is the pointer: a fine pointer means a mouse or
 * trackpad, which means somebody sitting down, which means reviewing and
 * editing rather than photographing a screen with gloves on.
 *
 * Both conditions are required, so a phone in landscape stays a phone.
 */
const QUERY = '(pointer: fine) and (min-width: 1024px)';

export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(QUERY).matches === true,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    // Resizing a window across the threshold, or docking a tablet to a
    // keyboard, has to move the UI with it rather than waiting for a reload.
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}
